import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Building2, Mail, MapPin, Phone } from 'lucide-react';
import axiosInstance from '../axiosConfig';
import CreateCardForm from '../components/CreateCardForm';
import coverImages from '../constants/coverImages';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { cn } from '../lib/utils';

const BildaPreview = ({ fullName, initials, profilePreview, coverPreview, contactInfo, customFields }) => {
  const infoRows = [
    { icon: Mail, label: 'Email', value: contactInfo.email },
    { icon: Phone, label: 'Telefono', value: contactInfo.phone },
    { icon: Building2, label: 'Organizzazione', value: contactInfo.organization },
    { icon: MapPin, label: 'Indirizzo', value: contactInfo.address }
  ].filter((row) => row.value);

  const gradientFallback = 'bg-gradient-to-br from-indigo-500 via-purple-500 to-fuchsia-500';

  return (
    <div className="relative w-full max-w-sm overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-500 via-purple-500 to-fuchsia-600 p-6 text-white shadow-2xl">
      <div className="absolute -top-20 right-0 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
      <div className="absolute bottom-0 left-0 h-48 w-48 rounded-full bg-white/10 blur-3xl" />

      <div className="relative mx-auto w-full max-w-xs overflow-hidden rounded-3xl bg-white/85 text-slate-900 shadow-xl backdrop-blur-2xl">
        <div className="relative h-36 w-full overflow-hidden rounded-t-3xl">
          {coverPreview ? (
            <img src={coverPreview} alt="Anteprima cover" className="h-full w-full object-cover" />
          ) : (
            <div className={cn('h-full w-full', gradientFallback)} />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
        </div>

        <div className="-mt-14 flex flex-col items-center px-6 pb-6">
          <div className="relative h-24 w-24 overflow-hidden rounded-full border-4 border-white shadow-xl">
            {profilePreview ? (
              <img src={profilePreview} alt="Anteprima profilo" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-indigo-500 text-2xl font-semibold text-white">
                {initials || 'EC'}
              </div>
            )}
          </div>

          <h3 className="mt-4 text-xl font-semibold text-slate-900">{fullName || 'Nome Cognome'}</h3>
          {contactInfo.jobTitle && (
            <p className="text-sm text-slate-500">{contactInfo.jobTitle}</p>
          )}
          {contactInfo.organization && (
            <span className="mt-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-600">
              {contactInfo.organization}
            </span>
          )}

          {infoRows.length > 0 && (
            <div className="mt-6 w-full space-y-3 rounded-2xl bg-white/80 p-4 text-sm text-slate-600 shadow-inner">
              {infoRows.map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-500">
                    <Icon size={16} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
                    <p className="text-sm font-medium text-slate-700">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {customFields.length > 0 && (
            <div className="mt-4 w-full space-y-2 text-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Link rapidi</p>
              <div className="space-y-2">
                {customFields.map((field, index) => (
                  <div
                    key={`${field.label}-${index}`}
                    className="flex items-center justify-between rounded-xl border border-indigo-100 bg-white/80 px-3 py-2 text-xs text-slate-600"
                  >
                    <span className="font-medium text-slate-700">{field.label || 'Link'}</span>
                    <span className="truncate pl-2 text-indigo-600">
                      {field.value || 'https://example.com'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ClassicPreview = ({
  fullName,
  initials,
  profilePreview,
  coverPreview,
  contactInfo,
  customFields
}) => {
  const infoRows = [
    { label: 'Telefono', value: contactInfo.phone },
    { label: 'Email', value: contactInfo.email },
    { label: 'Indirizzo', value: contactInfo.address }
  ].filter((row) => row.value);

  const gradientFallback = 'bg-gradient-to-tr from-sky-400 to-cyan-300';

  return (
    <div className="w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-slate-200">
      <div className="h-36 w-full overflow-hidden">
        {coverPreview ? (
          <img src={coverPreview} alt="Anteprima cover" className="h-full w-full object-cover" />
        ) : (
          <div className={cn('h-full w-full', gradientFallback)} />
        )}
      </div>

      <div className="relative flex flex-col items-center px-6 pb-6">
        <div className="-mt-12 h-24 w-24 overflow-hidden rounded-full border-4 border-white shadow-lg">
          {profilePreview ? (
            <img src={profilePreview} alt="Anteprima profilo" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-slate-700 text-2xl font-semibold text-white">
              {initials || 'EC'}
            </div>
          )}
        </div>

        <div className="mt-4 text-center">
          <h3 className="text-xl font-semibold text-slate-900">{fullName || 'Nome Cognome'}</h3>
          {contactInfo.jobTitle && (
            <p className="text-sm text-slate-500">{contactInfo.jobTitle}</p>
          )}
          {contactInfo.organization && (
            <p className="text-sm font-medium text-sky-600">{contactInfo.organization}</p>
          )}
        </div>

        <div className="mt-6 w-full space-y-3 rounded-2xl bg-slate-50 p-4 text-sm text-slate-600 shadow-inner">
          {infoRows.map(({ label, value }) => (
            <div key={label} className="flex flex-col">
              <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
              <span className="font-medium text-slate-700">{value}</span>
            </div>
          ))}

          {customFields.map((field, index) => (
            <div key={`${field.label}-${index}`} className="flex flex-col">
              <span className="text-xs uppercase tracking-wide text-slate-400">
                {field.label || 'Link'}
              </span>
              <span className="font-medium text-sky-600">
                {field.value || 'https://example.com'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MinimalPreview = ({ fullName, initials, profilePreview, coverPreview, contactInfo, customFields }) => {
  const infoRows = [
    { label: 'Telefono', value: contactInfo.phone, href: `tel:${contactInfo.phone}` },
    { label: 'Email', value: contactInfo.email, href: `mailto:${contactInfo.email}` },
    { label: 'Indirizzo', value: contactInfo.address }
  ].filter((row) => row.value);

  return (
    <div className="w-full max-w-sm overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
      <div className="relative h-36 w-full overflow-hidden">
        {coverPreview ? (
          <img src={coverPreview} alt="Anteprima cover" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-blue-500 via-sky-400 to-indigo-500" />
        )}
      </div>
      <div className="px-6 pb-6">
        <div className="flex justify-center">
          <div className="-mt-12 h-24 w-24 overflow-hidden rounded-full border-4 border-white shadow-lg">
            {profilePreview ? (
              <img src={profilePreview} alt="Anteprima profilo" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-blue-500 text-2xl font-semibold text-white">
                {initials || 'EC'}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 text-center">
          <h3 className="text-xl font-semibold text-slate-900">{fullName || 'Nome Cognome'}</h3>
          <p className="text-sm text-slate-500">{contactInfo.jobTitle}</p>
          {contactInfo.organization && (
            <span className="mt-2 inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-600">
              {contactInfo.organization}
            </span>
          )}
        </div>
        <div className="mt-6 space-y-3">
          {infoRows.map((row) => (
            <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600" key={row.label}>
              <span>{row.label}</span>
              {row.href ? (
                <a href={row.href} className="font-medium text-blue-600">
                  {row.value}
                </a>
              ) : (
                <span>{row.value}</span>
              )}
            </div>
          ))}
        </div>
        {customFields.length > 0 && (
          <div className="mt-4 space-y-2 text-sm">
            <p className="font-semibold text-slate-800">Link rapidi</p>
            <div className="space-y-2">
              {customFields.map((field, index) => (
                <div className="flex items-center justify-between rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-slate-600" key={`${field.label}-${index}`}>
                  <span className="font-medium text-slate-800">{field.label || 'Link'}</span>
                  <span className="truncate pl-2 text-blue-600">{field.value || 'https://example.com'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AuroraPreview = ({ fullName, initials, profilePreview, coverPreview, contactInfo, customFields }) => {
  const infoRows = [
    { label: 'Telefono', value: contactInfo.phone, href: `tel:${contactInfo.phone}` },
    { label: 'Email', value: contactInfo.email, href: `mailto:${contactInfo.email}` }
  ].filter((row) => row.value);

  return (
    <div className="relative w-full max-w-sm overflow-hidden rounded-3xl border border-slate-700 bg-slate-900/70 p-6 text-slate-100 shadow-2xl">
      <div className="absolute -top-20 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full bg-blue-500/30 blur-3xl" />
      <div className="relative space-y-4">
        <div className="relative h-32 w-full overflow-hidden rounded-2xl border border-white/10">
          {coverPreview ? (
            <img src={coverPreview} alt="Anteprima cover" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full bg-gradient-to-tr from-blue-500 via-purple-500 to-rose-500" />
          )}
        </div>
        <div className="flex justify-center">
          <div className="-mt-12 h-24 w-24 overflow-hidden rounded-3xl border-4 border-white/60 shadow-xl">
            {profilePreview ? (
              <img src={profilePreview} alt="Anteprima profilo" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-purple-500 text-2xl font-semibold text-white">
                {initials || 'EC'}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-1 text-center">
          <h3 className="text-xl font-semibold text-slate-50">{fullName || 'Nome Cognome'}</h3>
          <p className="text-sm text-slate-300">{contactInfo.jobTitle}</p>
          {contactInfo.organization && (
            <span className="inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-slate-100">
              {contactInfo.organization}
            </span>
          )}
        </div>
        {infoRows.length > 0 && (
          <div className="space-y-2 text-sm">
            {infoRows.map((row) => (
              <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-slate-300" key={row.label}>
                <span>{row.label}</span>
                <a href={row.href} className="text-blue-200">
                  {row.value}
                </a>
              </div>
            ))}
          </div>
        )}
        {customFields.length > 0 && (
          <div className="space-y-2 text-sm">
            <p className="font-semibold text-slate-100">Link rapidi</p>
            <div className="space-y-2">
              {customFields.map((field, index) => (
                <div
                  className="rounded-2xl border border-blue-300/20 bg-blue-500/10 px-4 py-3 text-xs text-blue-100"
                  key={`${field.label}-${index}`}
                >
                  <span className="block text-blue-200">{field.label || 'Link'}</span>
                  <span className="block text-ellipsis text-slate-100">{field.value || 'https://example.com'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const LiveCardPreview = ({ contactInfo, customFields, profilePreview, coverPreview, theme }) => {
  const fullName = [contactInfo.name, contactInfo.surname].filter(Boolean).join(' ');
  const initials = useMemo(() => {
    if (!fullName) return 'EC';
    return fullName
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('')
      .slice(0, 2);
  }, [fullName]);

  const filteredCustomFields = customFields.filter((field) => field.label || field.value);

  const previewProps = {
    fullName,
    initials,
    profilePreview,
    coverPreview,
    contactInfo,
    customFields: filteredCustomFields
  };

  if (theme === 'minimal') {
    return <MinimalPreview {...previewProps} />;
  }

  if (theme === 'classic') {
    return <ClassicPreview {...previewProps} />;
  }

  if (theme === 'aurora') {
    return <AuroraPreview {...previewProps} />;
  }

  return <BildaPreview {...previewProps} />;
};

const CreateCard = () => {
  const [contactInfo, setContactInfo] = useState({
    name: '',
    surname: '',
    organization: '',
    jobTitle: '',
    email: '',
    phone: '',
    address: ''
  });
  const [customFields, setCustomFields] = useState([{ label: '', value: '' }]);
  const [profilePic, setProfilePic] = useState(null);
  const [coverPic, setCoverPic] = useState(null);
  const [selectedCover, setSelectedCover] = useState('');
  const [profilePreview, setProfilePreview] = useState('');
  const [coverPreview, setCoverPreview] = useState('');
  const [theme, setTheme] = useState('bilda2025');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (profilePreview && profilePreview.startsWith('blob:')) {
        URL.revokeObjectURL(profilePreview);
      }
      if (coverPreview && coverPreview.startsWith('blob:')) {
        URL.revokeObjectURL(coverPreview);
      }
    };
  }, [profilePreview, coverPreview]);

  const updatePreview = (file, type) => {
    if (!file) return;
    const nextUrl = URL.createObjectURL(file);

    if (type === 'profile') {
      if (profilePreview && profilePreview.startsWith('blob:')) {
        URL.revokeObjectURL(profilePreview);
      }
      setProfilePic(file);
      setProfilePreview(nextUrl);
    } else {
      if (coverPreview && coverPreview.startsWith('blob:')) {
        URL.revokeObjectURL(coverPreview);
      }
      setCoverPic(file);
      setSelectedCover('');
      setCoverPreview(nextUrl);
    }
  };

  const onDrop = (acceptedFiles, type) => {
    const file = acceptedFiles[0];
    if (!file) return;
    updatePreview(file, type);
  };

  const { getRootProps: getRootPropsProfile, getInputProps: getInputPropsProfile } = useDropzone({
    onDrop: (files) => onDrop(files, 'profile'),
    accept: { 'image/*': [] },
    multiple: false
  });

  const { getRootProps: getRootPropsCover, getInputProps: getInputPropsCover } = useDropzone({
    onDrop: (files) => onDrop(files, 'cover'),
    accept: { 'image/*': [] },
    multiple: false
  });

  const handleContactInfoChange = (event) => {
    const { name, value } = event.target;
    setContactInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomFieldChange = (index, event) => {
    const { name, value } = event.target;
    setCustomFields((prev) =>
      prev.map((field, i) => (i === index ? { ...field, [name]: value } : field))
    );
  };

  const handleAddCustomField = () => {
    setCustomFields((prev) => [...prev, { label: '', value: '' }]);
  };

  const handleRemoveCustomField = (index) => {
    setCustomFields((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCoverSelectChange = (event) => {
    const value = event.target.value;
    setSelectedCover(value);

    if (coverPreview && coverPreview.startsWith('blob:')) {
      URL.revokeObjectURL(coverPreview);
    }

    if (value) {
      setCoverPic(value);
      setCoverPreview(value);
    } else {
      setCoverPic(null);
      setCoverPreview('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const formData = new FormData();
    formData.append('contactInfo', JSON.stringify(contactInfo));
    formData.append(
      'customFields',
      JSON.stringify(customFields.filter((field) => field.label && field.value))
    );
    formData.append('theme', theme);

    if (profilePic) {
      formData.append('profilePic', profilePic);
    }

    if (coverPic) {
      formData.append('coverPic', coverPic);
    }

    try {
      const response = await axiosInstance.post('/api/cards', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
        onUploadProgress: (eventProgress) => {
          const percentCompleted = Math.round((eventProgress.loaded * 100) / (eventProgress.total || 1));
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });
      console.log('Card created successfully:', response.data);
      navigate('/');
    } catch (error) {
      console.error('Error creating card:', error);
      if (error.code === 'ECONNABORTED') {
        alert('La richiesta ha impiegato troppo tempo. Riprova con immagini più piccole.');
      } else {
        alert('Errore durante la creazione della card. Riprova.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative container mx-auto space-y-8 px-4 py-6 lg:space-y-10">
      {isLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur">
          <div className="h-16 w-16 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Personalizza il tuo biglietto digitale</CardTitle>
            <CardDescription>
              Compila le informazioni e carica immagini per creare un biglietto moderno e sempre aggiornato.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CreateCardForm
              contactInfo={contactInfo}
              handleContactInfoChange={handleContactInfoChange}
              customFields={customFields}
              handleCustomFieldChange={handleCustomFieldChange}
              handleAddCustomField={handleAddCustomField}
              handleRemoveCustomField={handleRemoveCustomField}
              getRootPropsProfile={getRootPropsProfile}
              getInputPropsProfile={getInputPropsProfile}
              profilePic={profilePic}
              profilePreview={profilePreview}
              getRootPropsCover={getRootPropsCover}
              getInputPropsCover={getInputPropsCover}
              coverPic={coverPic}
              coverPreview={coverPreview}
              selectedCover={selectedCover}
              handleCoverSelectChange={handleCoverSelectChange}
              theme={theme}
              setTheme={setTheme}
              handleSubmit={handleSubmit}
              isSubmitting={isLoading}
              availableCovers={coverImages}
            />
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Anteprima in tempo reale</CardTitle>
              <CardDescription>
                Aggiorna i campi per vedere subito il risultato finale del tuo biglietto digitale.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LiveCardPreview
                contactInfo={contactInfo}
                customFields={customFields}
                profilePreview={profilePreview}
                coverPreview={coverPreview || selectedCover}
                theme={theme}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CreateCard;
